/**
 * Wallet Store
 *
 * Manages wallet/balance state for users and vendors. Provides the wallet
 * summary (balance, total in/out, pending amounts), paginated transaction
 * history, and vendor payout records. Transaction types include challenge
 * purchases, gift credits, and vendor payouts.
 *
 * @module useWalletStore
 *
 * @example
 * ```tsx
 * import { useWalletStore, selectWalletBalance, selectWalletTransactions } from '@/store/useWalletStore'
 *
 * function WalletPage({ userId }: { userId: string }) {
 *   const { fetchWalletSummary, fetchTransactions } = useWalletStore()
 *   const balance = useWalletStore(selectWalletBalance)
 *   const transactions = useWalletStore(selectWalletTransactions)
 *
 *   useEffect(() => {
 *     fetchWalletSummary(userId)
 *     fetchTransactions(userId)
 *   }, [userId])
 *
 *   return <WalletDashboard balance={balance} transactions={transactions} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  WalletSummary,
  WalletTransaction,
  Payout,
} from '../types/wallet.types'
import { walletApi } from '../api'

interface WalletState {
  summary: WalletSummary | null
  transactions: WalletTransaction[]
  payouts: Payout[]
  currentPage: number
  totalPages: number
  hasMore: boolean
  isLoading: boolean
  error: string | null

  // Actions
  fetchWalletSummary: (userId: string) => Promise<void>
  fetchTransactions: (userId: string, page?: number) => Promise<void>
  fetchPayouts: (vendorId: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  summary: null,
  transactions: [],
  payouts: [],
  currentPage: 1,
  totalPages: 1,
  hasMore: false,
  isLoading: false,
  error: null,
}

export const useWalletStore = createWithEqualityFn<WalletState>()((set) => ({
  ...initialState,

  fetchWalletSummary: async (userId: string) => {
    set({ isLoading: true, error: null })
    const response = await walletApi.getWalletSummary(userId)
    if (response.success) {
      set({ summary: response.data, isLoading: false })
    } else {
      set({ error: response.error ?? 'Failed to load wallet', isLoading: false })
    }
  },

  fetchTransactions: async (userId: string, page = 1) => {
    set({ isLoading: true, error: null })
    const response = await walletApi.getTransactions(userId, page)
    if (response.success) {
      set({
        transactions: page === 1 ? response.data : [],
        currentPage: response.pagination.page,
        totalPages: response.pagination.totalPages,
        hasMore: response.pagination.hasMore,
        isLoading: false,
      })
    } else {
      set({ error: response.error ?? 'Failed to load transactions', isLoading: false })
    }
  },

  fetchPayouts: async (vendorId: string) => {
    set({ isLoading: true, error: null })
    const response = await walletApi.getPayouts(vendorId)
    if (response.success) {
      set({ payouts: response.data, isLoading: false })
    } else {
      set({ error: response.error ?? 'Failed to load payouts', isLoading: false })
    }
  },

  clearError: () => set({ error: null }),
  reset: () => set(initialState),
}))

// ─── Selectors ────────────────────────────────────────────────────────────────

/** Select the user's current wallet balance (defaults to 0). */
export const selectWalletBalance = (state: WalletState) =>
  state.summary?.balance ?? 0

/** Select the full wallet summary (balance, totals, pending amounts). */
export const selectWalletSummary = (state: WalletState) => state.summary

/** Select the paginated list of wallet transactions. */
export const selectWalletTransactions = (state: WalletState) => state.transactions

/** Select vendor payout records. */
export const selectPayouts = (state: WalletState) => state.payouts
